// ============================================================
// NUDGE AI — All Constants and Data
// ============================================================

export const PHASES = {
  MARKETING: 0, AUTH: 1, CONSENT: 2, DEMOGRAPHICS: 3,
  BASELINE: 4, COACHING: 5, POSTTEST: 6, COMPLETE: 7
};

export const WEEKS = [
  "Financial Foundations",
  "Borrowing & Debt Management",
  "Building Assets",
  "Protection & Planning"
];

export const WEEK_TOPICS = [
  ["Budgeting basics", "Compound interest", "Inflation and purchasing power", "Trade-off analysis", "Setting financial priorities", "Weekly reflection"],
  ["Understanding credit scores", "Interest rate comparison", "Debt repayment strategies", "Gain vs. loss framing", "Borrowing decisions", "Weekly reflection"],
  ["Emergency savings", "Investment basics", "Risk and return", "Confidence calibration", "Portfolio allocation", "Weekly reflection"],
  ["Insurance literacy", "Emergency fund adequacy", "Financial goal-setting", "Bias pattern review", "Capstone reflection", "Action planning"]
];

export const BFI2_ITEMS = [
  "Is outgoing, sociable", "Is compassionate, has a soft heart", "Tends to be disorganized", "Is relaxed, handles stress well", "Has few artistic interests",
  "Has an assertive personality", "Is respectful, treats others with respect", "Tends to be lazy", "Stays optimistic after experiencing a setback", "Is curious about many different things",
  "Rarely feels excited or eager", "Tends to find fault with others", "Is dependable, steady", "Can be moody, has up-and-down mood swings", "Is inventive, finds clever ways to do things",
  "Tends to be quiet", "Feels little sympathy for others", "Is systematic, likes to keep things in order", "Can be tense", "Has a vivid imagination",
  "Is dominant, acts as a leader", "Is sometimes rude to others", "Has difficulty getting started on tasks", "Tends to feel depressed, blue", "Has little interest in abstract ideas",
  "Is full of energy", "Assumes the best about people", "Sometimes behaves irresponsibly", "Is emotionally stable, not easily upset", "Likes to reflect, play with ideas",
  "Is less active than other people", "Starts arguments with others", "Is reliable, can always be counted on", "Feels secure, comfortable with self", "Prefers work that is routine and simple",
  "Is energetic, high-spirited", "Is forgiving of others", "Can be somewhat careless", "Worries a lot", "Values art and beauty",
  "Finds it hard to influence people", "Is helpful and unselfish with others", "Keeps things neat and tidy", "Is temperamental, gets emotional easily", "Has sophisticated taste in art, music, or literature",
  "Is sociable, likes to be with people", "Can be cold and uncaring", "Follows through on plans and commitments", "Is often in a bad mood", "Enjoys thinking about complex problems",
  "Prefers to have others take charge", "Is warm, a warm person", "Sometimes does foolish things", "Rarely feels anxious or afraid", "Has little creativity",
  "Is talkative", "Can be rude to others", "Does things efficiently", "Feels depressed", "Has difficulty imagining things"
];

export const BFI2_DOMAINS = {
  E: [0,5,10,15,25,30,35,45,50,55,59],
  A: [1,6,11,16,21,26,31,36,41,46,51,56],
  C: [2,7,12,17,22,27,32,37,42,47,52,57],
  N: [3,8,13,18,23,28,33,38,43,48,53,58],
  O: [4,9,14,19,24,29,34,39,44,49,54,59]
};

export const BFI2_REVERSE = [2,3,5,7,10,11,15,16,17,21,24,25,30,33,34,37,41,46,48,50,53,54,56,58,59];

export const HUSTON_ITEMS = [
  { q:"Inflation causes the purchasing power of money to:", opts:["Increase","Decrease","Stay the same","It depends"], correct:1, domain:"foundations" },
  { q:"If you invest $1,000 at 4% annual interest compounded annually, after 2 years you will have approximately:", opts:["$1,040","$1,080","$1,081.60","$1,160"], correct:2, domain:"foundations" },
  { q:"Buying a single company stock usually provides a safer return than a stock mutual fund:", opts:["True","False"], correct:1, domain:"foundations" },
  { q:"An investment with a high return is likely to have:", opts:["High risk","Low risk","No risk","It depends on the company"], correct:0, domain:"foundations" },
  { q:"How confident are you in your understanding of basic financial concepts (interest, inflation, diversification)?", opts:["Very confident","Somewhat confident","Not very confident","Not at all confident"], type:"confidence", domain:"foundations" },
  { q:"Which of the following types of loans typically has the highest interest rate?", opts:["Mortgage","Auto loan","Credit card","Student loan"], correct:2, domain:"borrowing" },
  { q:"If you pay only the minimum payment on a credit card balance, you will pay more in total interest over time:", opts:["True","False"], correct:0, domain:"borrowing" },
  { q:"A 15-year mortgage typically has higher monthly payments but lower total interest than a 30-year mortgage:", opts:["True","False"], correct:0, domain:"borrowing" },
  { q:"Which strategy pays off debt fastest while minimizing total interest?", opts:["Paying smallest balance first","Paying highest interest rate first","Paying equal amounts to all debts","Only making minimum payments"], correct:1, domain:"borrowing" },
  { q:"How confident are you in your ability to manage borrowing decisions?", opts:["Very confident","Somewhat confident","Not very confident","Not at all confident"], type:"confidence", domain:"borrowing" },
  { q:"Over the last 100 years, which asset class has generally provided the highest average annual return?", opts:["Savings accounts","Government bonds","Stocks","Real estate"], correct:2, domain:"assets" },
  { q:"Dollar-cost averaging means investing a fixed amount at regular intervals regardless of market conditions:", opts:["True","False"], correct:0, domain:"assets" },
  { q:"Which of the following best describes the benefit of diversification?", opts:["It guarantees higher returns","It reduces risk by spreading investments","It eliminates all investment risk","It increases short-term gains"], correct:1, domain:"assets" },
  { q:"A bond is best described as:", opts:["Ownership in a company","A loan you make to an organization","A type of savings account","An insurance product"], correct:1, domain:"assets" },
  { q:"How confident are you in making decisions about saving and investing?", opts:["Very confident","Somewhat confident","Not very confident","Not at all confident"], type:"confidence", domain:"assets" },
  { q:"Which type of insurance protects against the financial consequences of someone being unable to work?", opts:["Life insurance","Health insurance","Disability insurance","Auto insurance"], correct:2, domain:"protection" },
  { q:"A general recommendation for an emergency fund is:", opts:["1 month of expenses","3 to 6 months of expenses","1 year of expenses","It depends on your debt level only"], correct:1, domain:"protection" },
  { q:"Term life insurance provides coverage for a specific period, while whole life insurance covers your entire lifetime:", opts:["True","False"], correct:0, domain:"protection" },
  { q:"Which of the following is NOT typically covered by a standard homeowner's insurance policy?", opts:["Fire damage","Theft","Flood damage","Liability claims"], correct:2, domain:"protection" },
  { q:"How confident are you in your understanding of insurance and financial protection strategies?", opts:["Very confident","Somewhat confident","Not very confident","Not at all confident"], type:"confidence", domain:"protection" },
];

export const AI_LIT_ITEMS = [
  "I understand the basic concept of how artificial intelligence works",
  "I can distinguish between tasks that AI can and cannot do well",
  "I am aware that AI systems can have biases built into them",
  "I understand that AI generates responses based on patterns in data, not true understanding",
  "I can evaluate whether information provided by an AI is likely accurate",
  "I understand that different AI tools may give different answers to the same question",
  "I know how to use AI tools effectively to help me learn",
  "I am comfortable adjusting how I interact with AI based on the responses I receive",
  "I understand the privacy implications of sharing personal information with AI tools",
  "I can identify when AI-generated content might be misleading or incorrect",
  "I understand that AI recommendations should be verified with other sources",
  "I am aware of the ethical considerations surrounding AI use in personal finance"
];

export const CFPB_ITEMS = [
  { q:"I could handle a major unexpected expense", scale:["Not at all","Very little","Somewhat","Very well","Completely"] },
  { q:"I am securing my financial future", scale:["Not at all","Very little","Somewhat","Very well","Completely"] },
  { q:"Because of my money situation, I feel like I will never have the things I want in life", scale:["Completely","Very well","Somewhat","Very little","Not at all"] },
  { q:"I can enjoy life because of the way I'm managing my money", scale:["Not at all","Very little","Somewhat","Very well","Completely"] },
  { q:"I am just getting by financially", scale:["Completely","Very well","Somewhat","Very little","Not at all"] },
  { q:"I am concerned that the money I have or will save won't last", scale:["Completely","Very well","Somewhat","Very little","Not at all"] },
  { q:"Giving a gift for a wedding, birthday, or other occasion would put a strain on my finances for the month", scale:["Always","Often","Sometimes","Rarely","Never"] },
  { q:"I have money left over at the end of the month", scale:["Never","Rarely","Sometimes","Often","Always"] },
  { q:"I am behind with my finances", scale:["Always","Often","Sometimes","Rarely","Never"] },
  { q:"My finances control my life", scale:["Always","Often","Sometimes","Rarely","Never"] },
];

export const FSES_ITEMS = [
  "It is hard to stick to my spending plan when unexpected expenses arise",
  "It is challenging to make progress toward my financial goals",
  "When unexpected expenses occur, I usually have to use credit",
  "When faced with a financial challenge, I have a hard time figuring out a solution",
  "I lack confidence in my ability to manage my finances",
  "I worry about running out of money in retirement"
];

export const OVERCONF_ITEMS = [
  { q:"What is the current annual inflation rate in the US (approximate)?", unit:"%", actual:3.2 },
  { q:"What is the average annual return of the S&P 500 over the last 30 years?", unit:"%", actual:10.5 },
  { q:"What percentage of Americans have less than $1,000 in savings?", unit:"%", actual:57 },
  { q:"What is the average credit card interest rate in the US?", unit:"%", actual:24.6 },
  { q:"What percentage of US households own stocks (directly or through funds)?", unit:"%", actual:58 },
  { q:"What is the current average 30-year fixed mortgage rate (approximate)?", unit:"%", actual:6.8 },
  { q:"What is the median household income in the United States?", unit:"$ (thousands)", actual:75 },
  { q:"What percentage of Americans do not have enough savings to cover a $400 emergency?", unit:"%", actual:37 },
  { q:"What is the average student loan debt for a college graduate?", unit:"$ (thousands)", actual:33 },
  { q:"What is the average annual cost of health insurance for a family in the US?", unit:"$ (thousands)", actual:24 },
];

export const LOSS_AV_ITEMS = [
  { gain:60, loss:20 }, { gain:60, loss:30 }, { gain:60, loss:40 },
  { gain:60, loss:50 }, { gain:60, loss:60 }, { gain:60, loss:70 }
];

export const HELP_SEEK_ITEMS = [
  "If I had a financial problem, I would seek help from a professional",
  "A financial advisor could help me make better decisions than I make on my own",
  "I think professional financial advice is worth paying for",
  "I would feel comfortable discussing my finances with a professional advisor",
  "Seeking financial advice is a sign of strength, not weakness",
  "I believe a financial planner could help me reach my goals faster",
  "I would be open to following a financial plan created with a professional",
  "I intend to seek professional financial advice in the next 12 months"
];

export const WTP_ITEMS = [
  { q:"Maximum you would pay for a one-time initial financial planning consultation (1 hour):", opts:["$0","$50","$100","$150","$200","$250+"] },
  { q:"Maximum you would pay per month for ongoing financial advisory services:", opts:["$0","$25","$50","$75","$100","$150+"] },
  { q:"Maximum you would pay for a comprehensive written financial plan:", opts:["$0","$250","$500","$1,000","$1,500","$2,000+"] },
];

export const DEMO_OPTS = {
  age: ["18-24","25-34","35-44","45-54","55-64","65+"],
  gender: ["Male","Female","Non-binary","Prefer not to say"],
  ethnicity: ["White","Black or African American","Hispanic or Latino","Asian","Indigenous","Two or more races","Other","Prefer not to say"],
  country: ["United States","Canada"],
  education: ["Less than high school","High school diploma","Some college","Associate degree","Bachelor's degree","Master's degree","Doctoral/Professional degree"],
  employment: ["Employed full-time","Employed part-time","Self-employed","Unemployed","Retired","Student","Other"],
  income: ["Under $25,000","$25,000-$49,999","$50,000-$74,999","$75,000-$99,999","$100,000-$149,999","$150,000+","Prefer not to say"],
  marital: ["Single","Married/Domestic partnership","Divorced","Widowed","Separated","Prefer not to say"],
  advice: ["Yes, I currently receive professional financial advice","No, I do not currently receive professional financial advice"],
};

// Colors
export const C = {
  bg: "#fafbfc",
  card: "#ffffff",
  navy: "#0f1f33",
  teal: "#0d9488",
  tealLight: "#ccfbf1",
  mint: "#02c39a",
  text: "#1e293b",
  body: "#475569",
  muted: "#94a3b8",
  border: "#e2e8f0",
  gold: "#d4a843",
  coral: "#e8735a",
  purple: "#6c5ce7",
};

// Scoring functions
export function scoreBFI2(answers) {
  const rev = new Set(BFI2_REVERSE);
  const scored = answers.map((a, i) => rev.has(i) ? 6 - a : a);
  const domainScores = {};
  for (const [domain, indices] of Object.entries(BFI2_DOMAINS)) {
    const vals = indices.map(i => scored[i]).filter(v => v != null);
    domainScores[domain] = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
  }
  const cIdx = BFI2_DOMAINS.C;
  const cScored = cIdx.map(i => scored[i]);
  domainScores.C_org = cScored.slice(0,4).reduce((s,v) => s+v,0)/4;
  domainScores.C_prod = cScored.slice(4,8).reduce((s,v) => s+v,0)/4;
  domainScores.C_resp = cScored.slice(8,12).reduce((s,v) => s+v,0)/4;
  return domainScores;
}

export function scoreHuston(answers) {
  const domains = { foundations:[], borrowing:[], assets:[], protection:[] };
  let correct = 0, total = 0;
  HUSTON_ITEMS.forEach((item, i) => {
    if (item.type === "confidence") return;
    if (answers[i] === item.correct) correct++;
    total++;
    domains[item.domain].push(answers[i] === item.correct ? 1 : 0);
  });
  const composite = Math.round((correct / total) * 100);
  const domainScores = {};
  let weakest = "foundations", weakestScore = 999;
  for (const [d, vals] of Object.entries(domains)) {
    const s = vals.length ? vals.reduce((a,b) => a+b, 0) / vals.length : 0;
    domainScores[d] = Math.round(s * 100);
    if (s < weakestScore) { weakestScore = s; weakest = d; }
  }
  const tier = composite <= 40 ? "low" : composite <= 70 ? "moderate" : "high";
  return { composite, tier, weakest, domainScores };
}

export function scoreOverconfidence(answers) {
  let outside = 0;
  answers.forEach((a, i) => {
    if (a?.low != null && a?.high != null) {
      const actual = OVERCONF_ITEMS[i].actual;
      if (actual < a.low || actual > a.high) outside++;
    }
  });
  return outside / OVERCONF_ITEMS.length;
}

export function scoreLossAversion(answers) {
  const firstReject = answers.findIndex(a => a === "reject");
  if (firstReject === -1) return 1.0;
  const item = LOSS_AV_ITEMS[firstReject];
  return item.loss / item.gain;
}

export function buildProfile(scores) {
  return {
    conscientiousness: scores.bfi2?.C || 3,
    conscientiousness_facets: {
      organization: scores.bfi2?.C_org || 3,
      productiveness: scores.bfi2?.C_prod || 3,
      responsibility: scores.bfi2?.C_resp || 3,
    },
    neuroticism: scores.bfi2?.N || 3,
    literacyComposite: scores.literacy?.composite || 50,
    literacyTier: scores.literacy?.tier || "moderate",
    weakestDomain: scores.literacy?.weakest || "general",
    aiLiteracy: scores.aiLiteracy || 50,
    overconfidence: scores.overconfidence || 0,
    lossAversion: scores.lossAversion || 2,
  };
}
